<script>
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import IconButton from "@/components/shared/IconButton.vue";
import { get } from "@/requests/server";

export default {
  name: "MailingListForm",
  components: { FormsValidatedInput, IconButton },
  props: {
    mailingList: { type: Object, default: null },
  },
  data() {
    return {
      communityId: this?.mailingList?.community_id,
      community: null,
      provider: this?.mailingList?.provider,
      apiKey: this?.mailingList?.api_key,
      listId: null,
      tag: this?.mailingList?.tag,
      fetchingLists: false,
      lists: null,
      listsPage: 0,
      listsPerPage: 10,
      listsTotalCount: null,
      lastFetchParams: null,
      error: null,
    };
  },
  computed: {
    hasMoreLists() {
      return (
        this.listsTotalCount && this.listsTotalCount > (this.listsPage + 1) * this.listsPerPage
      );
    },
  },
  methods: {
    async prevPage() {
      if (this.listsPage > 0) {
        this.listsPage--;
      }
      await this.fetchLists();
    },
    async nextPage() {
      if (this.hasMoreLists) {
        this.listsPage++;
      }
      await this.fetchLists();
    },
    async fetchLists() {
      let params = {
        id: this.mailingList.id,
        page: this.listsPage,
        per_page: this.listsPerPage,
      };

      if (this.lastFetchParams === JSON.stringify(params)) {
        return;
      }

      this.fetchingLists = true;

      const { data } = await get(`mailinglists/lists`, {
        axiosRequestConfig: {
          params: params,
        },
        cleanupCallback: () => (this.fetchingLists = false),
        requestOptions: {
          cancelId: "mailinglists/lists",
        },
      });

      if (data.error) {
        this.error = data.error;
        return;
      }

      this.lists = data.lists;
      this.listsTotalCount = data.count;
      this.lastFetchParams = JSON.stringify(params);
    },
  },
};
</script>

<template>
  <validation-provider>
    <b-form>
      <forms-validated-input
        :object-value="community"
        :value="communityId"
        disabled
        type="relation"
        name="community_id"
        :label="$t('components.admin.mailing_list_form.community_label')"
        :rules="{
          required: true,
        }"
        :query="{
          slug: 'communities',
          value: 'id',
          text: 'name',
          params: {
            for: 'admin',
          },
        }"
        @relation="community = $event"
      >
      </forms-validated-input>

      <b-form-group :label="$t('components.admin.mailing_list_form.provider_label')">
        <b-select v-model="provider" disabled>
          <b-select-option value="brevo">{{
            $t("components.admin.mailing_list_form.brevo_option")
          }}</b-select-option>
          <b-select-option value="mailchimp">{{
            $t("components.admin.mailing_list_form.mailchimp_option")
          }}</b-select-option>
        </b-select>
      </b-form-group>
      <b-form-group
        :label="$t('components.admin.mailing_list_form.api_key_label')"
        :validated="!!lists"
        :state="error ? false : lists ? true : null"
      >
        <b-input v-model="apiKey" disabled type="text" :state="error ? false : lists ? true : null">
        </b-input>
      </b-form-group>

      <b-form-group>
        <icon-button
          :onclick="fetchLists"
          :disabled="fetchingLists"
          icon="arrow-clockwise"
          variant="ghost-secondary"
        >
          {{ $t("components.admin.mailing_list_form.fetch_lists") }}
        </icon-button>
      </b-form-group>

      <b-alert v-if="error" variant="danger" show>
        <h4 class="alert-heading">
          <b-icon icon="exclamation-triangle-fill" />
          {{ $t("components.admin.mailing_list_form.fetch_lists_error_title") }}
        </h4>
        {{ error }}
      </b-alert>

      <b-form-group v-if="lists" :label="$t('components.admin.mailing_list_form.list.label')">
        <b-radio-group
          v-model="listId"
          class="list-selection"
          buttons
          button-variant="outline-secondary"
        >
          <b-radio v-for="list in lists" :key="list.id" :value="list.id"
            >{{ list.name }} <span class="list-id">(#{{ list.id }})</span></b-radio
          >
        </b-radio-group>
      </b-form-group>
      <div v-if="lists && listsTotalCount > listsPerPage" class="list-pagination">
        <icon-button
          :onclick="prevPage"
          :disabled="listsPage === 0 || fetchingLists"
          icon="arrow-left-circle"
          variant="ghost-secondary"
          size="sm"
          >{{ $t("components.admin.mailing_list_form.list.pagination.previous") }}</icon-button
        >
        <icon-button
          :onclick="nextPage"
          :disabled="!hasMoreLists || fetchingLists"
          icon="arrow-right-circle"
          variant="ghost-secondary"
          size="sm"
          >{{ $t("components.admin.mailing_list_form.list.pagination.next") }}</icon-button
        >
      </div>

      <forms-validated-input
        v-if="provider === 'mailchimp'"
        v-model="tag"
        disabled
        type="string"
        :label="$t('components.admin.mailing_list_form.tag.label')"
        name="tag"
        :description="$t('components.admin.mailing_list_form.tag.description')"
        :rules="{
          required: true,
        }"
      ></forms-validated-input>
    </b-form>
  </validation-provider>
</template>

<style scoped lang="scss">
.list-pagination {
  display: flex;
  justify-content: space-between;
  margin-top: 0.25rem;
  margin-bottom: 1rem;
}
.list-selection {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
  gap: 0.5rem;

  .list-id {
    color: $content-neutral-secondary;
    transition: all 0.15s ease-in-out;
  }

  .btn {
    border-radius: 0.25rem !important; // undo styling form button-group, which we can't remove

    &.active,
    &:hover {
      .list-id {
        color: $white;
      }
    }
  }
}
</style>
